/**
 * Sair da mesa com uma edição não salva pergunta antes (E202.F2, GUS-145, ADR-0145 §6; RF03).
 *
 * O roteador do app é o `BrowserRouter`, sem o bloqueio de navegação do roteador de dados: o clique
 * num link interno que leva para fora da mesa é segurado aqui (fase de captura), e a pessoa escolhe
 * ficar, sair guardando o rascunho ou sair descartando. Fechar ou recarregar a aba pede a confirmação
 * do navegador (`beforeunload`). De todo jeito, o rascunho vai ao servidor a cada pausa e, ao fechar o
 * bloco, o que estava pendente é guardado na hora.
 *
 * E202.F2.C04/C05 (F-E202.F2-04/05): "Guardar o rascunho e sair" e "Descartar e sair" esperam a
 * confirmação do servidor, com os botões travados; na falha, o diálogo fica com o aviso e o texto
 * continua no trecho aberto. O "Voltar" do navegador passa pela mesma pergunta
 * (`useGuardaDoHistorico`).
 *
 * E203.F2 (ADR-0146 §3): o painel do dado com alteração não salva também segura a saída; ele não
 * tem rascunho, então só "Ficar" e "Descartar e sair" (que fecha o painel) valem para ele.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { TrechoNaTelaData } from '@/lib/correcaoDoRascunho';

import { naGuarda, useGuardaDoHistorico } from './guardaDoHistorico';
import type { EdicaoNoDocumento } from './useEdicaoNoDocumento';

const t = ptBR.documentoDaMesa.sair;

/** O destino do "Voltar" do navegador: a entrada anterior à mesa. */
const VOLTAR = Symbol('voltar');
type Destino = string | typeof VOLTAR;

/** O clique simples (botão principal, sem tecla que abre noutra aba ou janela). */
function cliqueSimples(e: MouseEvent): boolean {
  return (
    !e.defaultPrevented && e.button === 0 && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
  );
}

/** O link do clique que navega nesta aba; `null`: não é link, ou abre noutra aba ou baixa. */
function linkDoClique(e: MouseEvent): HTMLAnchorElement | null {
  const link = e.target instanceof Element ? e.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement)) return null;
  return link.target === '_blank' || link.hasAttribute('download') ? null : link;
}

/** O endereço interno para fora desta página que o clique abriria; `null`: deixa passar. */
function destinoDoClique(e: MouseEvent, aqui: string): string | null {
  const link = cliqueSimples(e) ? linkDoClique(e) : null;
  if (link === null) return null;
  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin || url.pathname === aqui) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

export interface PainelSujo {
  sujo: boolean;
  descartar: () => void;
}

export function SairSemPerder({
  edicao,
  trechos,
  painel = null,
}: {
  edicao: EdicaoNoDocumento;
  trechos: readonly TrechoNaTelaData[];
  /** E203.F2: o painel do dado aberto na mesa. */
  painel?: PainelSujo | null;
}): JSX.Element {
  const navigate = useNavigate();
  const [destino, setDestino] = useState<Destino | null>(null);
  const [agindo, setAgindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const soPainel = !edicao.sujo && painel?.sujo === true;
  const sujo = edicao.sujo || soPainel;
  const aqui = useLocation().pathname;
  const guarda = useGuardaDoHistorico(sujo, () => {
    setErro(null);
    setDestino(VOLTAR);
  });

  useEffect(() => {
    if (!sujo) return undefined;
    const antesDeFechar = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const clique = (e: MouseEvent) => {
      const para = destinoDoClique(e, aqui);
      if (para === null) return;
      e.preventDefault();
      e.stopPropagation();
      setErro(null);
      setDestino(para);
    };
    window.addEventListener('beforeunload', antesDeFechar);
    document.addEventListener('click', clique, true);
    return () => {
      window.removeEventListener('beforeunload', antesDeFechar);
      document.removeEventListener('click', clique, true);
    };
  }, [sujo, aqui]);

  const ficar = () => {
    if (agindo) return;
    if (destino === VOLTAR) guarda.repor();
    setDestino(null);
  };

  const sair = async (descartar: boolean) => {
    const para = destino;
    if (para === null) return;
    const aberto = trechos.find((x) => x.id === edicao.aberto);
    setAgindo(true);
    setErro(null);
    guarda.marcarSaida(true);
    if (painel?.sujo) painel.descartar();
    try {
      // Só o painel sujo: fechá-lo já descartou, não há rascunho a guardar.
      if (!soPainel && descartar) {
        if (aberto !== undefined) await edicao.descartarJa(aberto);
      } else if (!soPainel) {
        await edicao.guardarJa();
      }
    } catch {
      guarda.marcarSaida(false);
      setErro(descartar ? t.falhouDescartar : t.falhouGuardar);
      return;
    } finally {
      setAgindo(false);
    }
    setDestino(null);
    if (para === VOLTAR) navigate(-1);
    else navigate(para, { replace: naGuarda() });
  };

  return (
    <Dialog open={destino !== null} onOpenChange={(aberto) => !aberto && ficar()}>
      <DialogContent data-testid="sair-sem-perder">
        <DialogHeader>
          <DialogTitle>{t.titulo}</DialogTitle>
          <DialogDescription>{soPainel ? t.textoDoPainel : t.texto}</DialogDescription>
        </DialogHeader>
        {erro !== null ? (
          <p role="alert" className="text-body-sm text-danger-700" data-testid="sair-erro">
            {erro}
          </p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={ficar}
            disabled={agindo}
            data-testid="sair-ficar"
          >
            {t.ficar}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void sair(true)}
            disabled={agindo}
            data-testid="sair-descartar"
          >
            {t.descartar}
          </Button>
          {soPainel ? null : (
            <Button
              type="button"
              onClick={() => void sair(false)}
              disabled={agindo}
              loading={agindo}
              data-testid="sair-guardar"
            >
              {t.guardar}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
